import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { useTranslation } from "react-i18next";

import "../styles/UserTeam.css";

const UserTeam = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const initialTeamId = Number(searchParams.get("teamId")) || 1;
  const [selectedTeam, setSelectedTeam] = useState(initialTeamId);
  const [sortKey, setSortKey] = useState("value");
  const [cyclists, setCyclists] = useState([]);
  const [pseudo, setPseudo] = useState("");

  const userId = params.userId;
  const apiUrl = process.env.REACT_APP_API_URL;

  const handleRowClick = (cyclistId) => {
    navigate(`/cyclistDetails/${cyclistId}`);
  };

  useEffect(() => {
    const nextTeamId = Number(searchParams.get("teamId")) || 1;
    setSelectedTeam(nextTeamId);
  }, [searchParams]);

  useEffect(() => {
    const fetchCyclists = async () => {
      if (!user) {
        navigate("/logIn");
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/cyclists-userteam/${userId}`, {
          credentials: "include",
        });

        if (!response.ok) {
          setCyclists([]);
          throw new Error("Fetch error");
        }

        const data = await response.json();
        setCyclists(Array.isArray(data?.cyclists) ? data.cyclists : []);
        setPseudo((data?.pseudo ?? "").toString());
      } catch (error) {
        console.error("Erreur:", error);
      }
    };

    fetchCyclists();
  }, [apiUrl, user, navigate, userId]);

  const getSortedCyclists = () => {
    return cyclists
      .filter((cyclist) => Number(cyclist.teamId) === selectedTeam)
      .sort((a, b) => {
        switch (sortKey) {
          case "value":
            return b.finalValue - a.finalValue;
          case "points":
            return b.cyclistPoints - a.cyclistPoints;
          default:
            return 0;
        }
      });
  };

  const handleSortChange = (event) => {
    setSortKey(event.target.value);
  };

  const handleButtonClick = (teamId) => {
    setSelectedTeam(teamId);
  };

  const sortedCyclists = getSortedCyclists();

  return (
    <section className="userteam">
      <div className="userteam-link-back-to-ranking-container">
        <Link to="/profil?section=Ranking" className="userteam-link-back-to-ranking">
          {t("userTeam.backToRanking")}
        </Link>
      </div>

      {pseudo ? <h1>{t("userTeam.title", { pseudo })}</h1> : null}

      <div className="userteam-choice">
        <button
          onClick={() => handleButtonClick(1)}
          style={{ backgroundColor: selectedTeam === 1 ? "#0056b3" : "#007afd" }}
        >
          {t("userTeam.teams.worldTour")}
        </button>
        <button
          onClick={() => handleButtonClick(2)}
          style={{ backgroundColor: selectedTeam === 2 ? "#0056b3" : "#007afd" }}
        >
          {t("userTeam.teams.neoPro")}
        </button>
        <button
          onClick={() => handleButtonClick(3)}
          style={{ backgroundColor: selectedTeam === 3 ? "#0056b3" : "#007afd" }}
        >
          {t("userTeam.teams.giro", { defaultValue: "Giro" })}
        </button>
        <button
          onClick={() => handleButtonClick(4)}
          style={{ backgroundColor: selectedTeam === 4 ? "#0056b3" : "#007afd" }}
        >
          {t("userTeam.teams.tdf", { defaultValue: "Tour de France" })}
        </button>
        <button
          onClick={() => handleButtonClick(5)}
          style={{ backgroundColor: selectedTeam === 5 ? "#0056b3" : "#007afd" }}
        >
          {t("userTeam.teams.vuelta", { defaultValue: "Vuelta" })}
        </button>
      </div>

      <div className="userteam-sort-control">
        <label>{t("userTeam.sort.label")}</label>
        <select onChange={handleSortChange} value={sortKey}>
          <option value="value">{t("userTeam.sort.value")}</option>
          <option value="points">{t("userTeam.sort.points")}</option>
        </select>
      </div>

      {cyclists.length > 0 ? (
        <div className="userteam-table">
          <div className="userteam-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("userTeam.table.cyclist")}</th>
                  <th>{t("userTeam.table.team")}</th>
                  <th>{t("userTeam.table.value")}</th>
                  <th>{t("userTeam.table.points")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedCyclists.map((cyclist) => (
                  <tr
                    key={cyclist.cyclistId}
                    onClick={() => handleRowClick(cyclist.cyclistId)}
                  >
                    <td>
                      <div className="userteam-name-info">
                        <span className="userteam-name-bonus">
                          {cyclist.isBonus ? t("userTeam.bonus") : ""}
                        </span>
                        <img
                          className="select-cyclists-flag-icon"
                          src={`${process.env.PUBLIC_URL}/flags/${cyclist.nationality
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "")
                            .replace(/ /g, "_")
                            .toLowerCase()}.png`}
                          alt={t("userTeam.flagAlt", { nationality: cyclist.nationality })}
                          width="20"
                          height="20"
                        />
                        <span>{cyclist.name}</span>
                      </div>
                    </td>
                    <td>{cyclist.team}</td>
                    <td>{cyclist.finalValue}</td>
                    <td>{cyclist.cyclistPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p>{t("userTeam.empty")}</p>
      )}
    </section>
  );
};

export default UserTeam;
